import { FaCheckCircle } from "react-icons/fa";
import { Button } from "../../../../app/components";
import { useState } from "react";
import { CustomModal } from "../../../Common/Modal";
import { Reserva } from "../../../../types";
import { Form, Row, Col, Alert } from "react-bootstrap";
import { formatPrice } from "../../../../utils/priceUtils";
import { updateReserva } from "../../../../services/firestoreService";
import { showToast } from "../../../../utils/toast";
import { useConfirmModal } from "../../../../hooks/useConfirmModal";
import ConfirmModal from "../../../Common/ConfirmModal";

interface RegisterReturnProps {
   reserva: Reserva;
   onStatusChange?: () => void;
   onClose?: () => void;
}

export const RegisterReturn = ({ reserva, onStatusChange, onClose }: RegisterReturnProps) => {
   const [show, setShow] = useState(false);
   const [loading, setLoading] = useState(false);
   const [observacoes, setObservacoes] = useState('');
   const { modalState, showConfirm, hideConfirm, handleConfirm } = useConfirmModal();

   const handleRegisterReturn = () => {
      if (!reserva.id) return;

      // Mostrar modal de confirmação
      showConfirm(
         'Confirmar Devolução',
         `Deseja realmente registrar a devolução para o cliente ${reserva.clienteNome}?\n\nEsta ação irá alterar o status da reserva para "Devolvido" e marcar todos os produtos como disponíveis novamente.`,
         async () => {
            if (!reserva.id) {
               showToast.error('ID da reserva não encontrado');
               setLoading(false);
               return;
            }

            const reservaId = reserva.id;

            try {
               setLoading(true);

               // Atualizar status para devolvido
               const updatedReserva: Partial<Reserva> = {
                  ...reserva,
                  status: 'devolvido' as const,
                  observacoes: observacoes || reserva.observacoes
               };

               const result = await updateReserva(reservaId, updatedReserva);

               if (result.success) {
                  showToast.success('Devolução registrada com sucesso!');
                  setShow(false);
                  
                  // Fechar modal de detalhes da reserva
                  if (onClose) {
                     setTimeout(() => {
                        onClose();
                     }, 100);
                  }
                  
                  if (onStatusChange) {
                     onStatusChange();
                  }
               } else {
                  showToast.error(result.error || 'Erro ao registrar devolução');
                  setLoading(false);
               }
            } catch (error) {
               showToast.error('Erro ao registrar devolução');
               setLoading(false);
            }
         },
         'success',
         'Sim, Confirmar',
         'Cancelar'
      );
   };

   if (reserva.status !== 'retirado') return null;

   return (
      <div className="d-flex flex-column flex-sm-row gap-2">
         <Button
            btnColor="success"
            onClick={() => setShow(true)}
            extraCss="d-flex align-items-center gap-2"
            disabled={reserva.status !== 'retirado'}
         >
            <FaCheckCircle size={20} />
            Registrar Devolução
         </Button>

         <CustomModal
            show={show}
            onHide={() => setShow(false)}
            size="lg"
            headerLabel="Registrar Devolução"
            footer={
               <div className="d-flex gap-2">
                  <Button btnColor="secondary" onClick={() => setShow(false)}>
                     Cancelar
                  </Button>
                  <Button
                     btnColor="success"
                     onClick={handleRegisterReturn}
                     disabled={loading}
                  >
                     {loading ? (
                        <>
                           <span className="spinner-border spinner-border-sm me-2" role="status" />
                           Processando...
                        </>
                     ) : (
                        <>
                           <FaCheckCircle className="me-2" />
                           Confirmar Devolução
                        </>
                     )}
                  </Button>
               </div>
            }
         >
            <div className="row">
               <div className="col-12">
                  {/* Resumo da Reserva */}

                  {/* Mensagem de confirmação */}
                  <Alert variant="success" className="mb-3">
                     <strong>✓ Confirmação de Devolução</strong>
                     <p className="mb-0 mt-2">
                        Ao confirmar, o status desta reserva será alterado para <strong>"Devolvido"</strong>. 
                        Todos os produtos serão marcados como devolvidos e disponíveis novamente.
                     </p>
                  </Alert>

                  {/* Observações */}
                  <div className="border rounded p-3 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                     <h6 className="mb-3">
                        Observações da Devolução (Opcional)
                     </h6>
                     <Form>
                        <Row>
                           <Col md={12}>
                              <Form.Label>Observações</Form.Label>
                              <Form.Control
                                 as="textarea"
                                 rows={3}
                                 value={observacoes}
                                 onChange={(e) => setObservacoes(e.target.value)}
                                 placeholder="Observações sobre a devolução (ex: estado dos produtos, itens faltantes, etc.)"
                              />
                           </Col>
                        </Row>
                     </Form>
                  </div>
               </div>
            </div>
         </CustomModal>

         {/* Modal de Confirmação */}
         <ConfirmModal
            show={modalState.show}
            onHide={hideConfirm}
            onConfirm={handleConfirm}
            title={modalState.title}
            message={modalState.message}
            type={modalState.type}
            confirmText={modalState.confirmText}
            cancelText={modalState.cancelText}
            loading={loading}
         />
      </div>
   );
};
